import { useMemo } from 'react'

import { LoginScope, LoginSource } from '@/consts/team'
import { useLoginSource, useScopeInfo, useTeamTokenData } from '@/stores/team'
import { getToken } from '@/utils/auth'

export type AppContextType = 'platform' | 'team'

export interface AppContextInfo {
  // Context identification
  context: AppContextType
  isInPlatform: boolean
  isOnTeamPage: boolean

  // Token status
  platformToken: string | undefined
  teamTokenData: ReturnType<typeof useTeamTokenData>
  hasPlatformToken: boolean
  hasTeamToken: boolean
  hasValidTeamToken: boolean

  // User type identification
  isTeamOnlyUser: boolean
  isPlatformUser: boolean
  loginSource: LoginSource | null

  // Current team info
  currentTeamId: number | undefined
  currentTeamResID: string | undefined

  // Path info
  currentPath: string
}

/**
 * Unified hook for determining current application context
 *
 * Centralizes all context-checking logic that was previously duplicated across:
 * - TeamSwitcher.tsx (lines 37-44)
 * - TeamLayout.tsx (lines 21-36)
 * - UserDropdown.tsx (lines 84-107)
 *
 * This hook provides a single source of truth for:
 * - Current context (platform vs team)
 * - Token availability and validity
 * - User type (platform user vs team-only user)
 * - Current page location
 */
export function useAppContext(): AppContextInfo {
  const teamTokenData = useTeamTokenData()
  const loginSource = useLoginSource()
  const platformToken = getToken()
  const scopeInfo = useScopeInfo()

  return useMemo(() => {
    // State-based context determination using scopeInfo (upgraded from path-based)
    const currentPath = window.location.pathname
    const isOnTeamPage = scopeInfo?.scope === LoginScope.Team
    const isInPlatform = !isOnTeamPage

    // Token validation
    const hasPlatformToken = !!platformToken
    const hasTeamToken = !!teamTokenData
    const hasValidTeamToken = teamTokenData !== null && teamTokenData.expiryTime > Date.now()

    // User type identification
    const isTeamOnlyUser = loginSource === LoginSource.Team && !hasPlatformToken
    const isPlatformUser = hasPlatformToken

    // Context type
    const context: AppContextType = isOnTeamPage ? 'team' : 'platform'

    // Current team info (only valid when on team pages)
    const currentTeamId = isOnTeamPage ? teamTokenData?.teamId : undefined
    const currentTeamResID = isOnTeamPage ? teamTokenData?.teamResID : undefined

    return {
      context,
      isInPlatform,
      isOnTeamPage,
      platformToken,
      teamTokenData,
      hasPlatformToken,
      hasTeamToken,
      hasValidTeamToken,
      isTeamOnlyUser,
      isPlatformUser,
      loginSource,
      currentTeamId,
      currentTeamResID,
      currentPath,
    }
  }, [teamTokenData, loginSource, platformToken, scopeInfo])
}
